// eslint-disable-next-line @typescript-eslint/no-restricted-imports
/* eslint-disable rxjs/finnish */
import { animate, fadeIn } from '@lit-labs/motion';
import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/**
 * Component for showing shareability status in a pretty footer
 */
@customElement('pique-action-shareability')
export class PiqueActionShareability extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: auto;
    }

    .shareability-footer {
      display: flex;
      justify-content: flex-end;
      position: relative;
    }

    .share-status-pill {
      display: inline-flex;
      align-items: center;
      padding: 6px 8px;
      border-radius: 18px;
      font-size: 12px;
      font-weight: 500;
      color: var(--affine-text-secondary-color);
      background-color: var(--affine-background-secondary-color);
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
      cursor: pointer;
      transition: all 0.2s ease;
      position: relative;
    }

    .share-status-pill:hover {
      transform: translateY(-2px);
      box-shadow: 0 2px 5px rgba(0,0,0,0.08);
      background-color: var(--affine-background-tertiary-color);
    }

    .share-icon-container {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .share-icon {
      width: 16px;
      height: 16px;
      opacity: 0.4;
      transition: opacity 0.2s ease, transform 0.2s ease;
      cursor: pointer;
      flex-shrink: 0;
    }

    .share-icon.active {
      opacity: 1;
    }

    .share-icon:hover {
      opacity: 0.7;
      transform: scale(1.1);
    }

    .share-icon.disabled {
      pointer-events: none;
    }

    .share-icon svg {
      width: 100%;
      height: 100%;
    }

    .divider {
      width: 1px;
      height: 14px;
      background-color: var(--affine-border-color);
      margin: 0 6px;
      opacity: 0.5;
      flex-shrink: 0;
    }

    /* Lock icon styles */
    .lock-icon {
      width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.3s ease;
    }
    
    .lock-icon.private {
      opacity: 1;
    }
    
    .lock-icon.shared {
      opacity: 0.6;
    }
    
    .share-status-pill:hover .lock-icon {
      transform: rotate(-5deg);
    }

    /* Status indicator dot */
    .status-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      margin-right: 8px;
      transition: background-color 0.3s ease;
    }
    
    .status-dot.private {
      background-color: var(--affine-warning-color);
    }
    
    .status-dot.shared {
      background-color: var(--affine-success-color);
    }
    
    /* Tooltip explaining lock status */
    .lock-tooltip {
      position: absolute;
      top: 100%;
      right: 0;
      background-color: var(--affine-background-secondary-color);
      color: var(--affine-text-primary-color);
      font-size: 11px;
      padding: 4px 8px;
      border-radius: 4px;
      pointer-events: none;
      opacity: 0;
      transform: translateY(-4px);
      transition: opacity 0.2s ease, transform 0.2s ease;
      white-space: nowrap;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
      z-index: 10;
      margin-top: 8px;
    }
    
    .share-status-pill:hover .lock-tooltip {
      opacity: 1;
      transform: translateY(0);
    }
  `;

  @property({ type: Boolean })
  accessor isPrivate: boolean = true;

  @property({ type: Array })
  accessor activeServices: string[] = [];

  @state()
  accessor isExpanded: boolean = false;

  private togglePrivacy() {
    this.isPrivate = !this.isPrivate;
    // Reset active services when switching to private
    if (this.isPrivate) {
      this.activeServices = [];
    }
    
    this.dispatchEvent(new CustomEvent('privacy-changed', {
      detail: { isPrivate: this.isPrivate },
      bubbles: true,
      composed: true
    }));
  }

  private toggleService(service: string, event: Event) {
    if (this.isPrivate) return; // Don't allow toggling when private
    
    // Stop the event from toggling the privacy when clicking an icon
    event.stopPropagation();
    
    const isActive = this.activeServices.includes(service);
    if (isActive) {
      this.activeServices = this.activeServices.filter(s => s !== service);
    } else {
      this.activeServices = [...this.activeServices, service];
    }
    
    this.dispatchEvent(new CustomEvent('services-changed', {
      detail: { activeServices: this.activeServices },
      bubbles: true,
      composed: true
    }));
  }

  // Render the lock icon (private status indicator)
  private renderLockIcon() {
    return html`
      <div class="lock-icon ${this.isPrivate ? 'private' : 'shared'}">
        ${this.isPrivate ? 
          html`<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>` : 
          html`<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 9.9-1"></path>
          </svg>`
        }
      </div>
    `;
  }

  // Render service icons
  private renderServiceIcons() {
    return html`
      <div class="divider"></div>
      <div class="share-icon-container">
        <!-- Google Calendar -->
        <div 
          class="share-icon ${this.activeServices.includes('google-calendar') ? 'active' : ''} ${this.isPrivate ? 'disabled' : ''}"
          @click=${(e: Event) => this.toggleService('google-calendar', e)}
          title="${this.isPrivate ? 'Private - Enable sharing to use Google Calendar' : 'Google Calendar'}"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect>
            <line x1="16" x2="16" y1="2" y2="6"></line>
            <line x1="8" x2="8" y1="2" y2="6"></line>
            <line x1="3" x2="21" y1="10" y2="10"></line>
          </svg>
        </div>
        
        <!-- Slack -->
        <div 
          class="share-icon ${this.activeServices.includes('slack') ? 'active' : ''} ${this.isPrivate ? 'disabled' : ''}"
          @click=${(e: Event) => this.toggleService('slack', e)}
          title="${this.isPrivate ? 'Private - Enable sharing to use Slack' : 'Slack'}"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14.5 10c-.83 0-1.5-.67-1.5-1.5v-5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5z"></path>
            <path d="M20.5 10H19V8.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"></path>
            <path d="M9.5 14c.83 0 1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5S8 21.33 8 20.5v-5c0-.83.67-1.5 1.5-1.5z"></path>
            <path d="M3.5 14H5v1.5c0 .83-.67 1.5-1.5 1.5S2 16.33 2 15.5 2.67 14 3.5 14z"></path>
            <path d="M14 14.5c0-.83.67-1.5 1.5-1.5h5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-5c-.83 0-1.5-.67-1.5-1.5z"></path>
            <path d="M15.5 19H14v1.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"></path>
            <path d="M10 9.5C10 8.67 9.33 8 8.5 8h-5C2.67 8 2 8.67 2 9.5S2.67 11 3.5 11h5c.83 0 1.5-.67 1.5-1.5z"></path>
            <path d="M8.5 5H10V3.5C10 2.67 9.33 2 8.5 2S7 2.67 7 3.5 7.67 5 8.5 5z"></path>
          </svg>
        </div>
        
        <!-- Trello -->
        <div 
          class="share-icon ${this.activeServices.includes('trello') ? 'active' : ''} ${this.isPrivate ? 'disabled' : ''}"
          @click=${(e: Event) => this.toggleService('trello', e)}
          title="${this.isPrivate ? 'Private - Enable sharing to use Trello' : 'Trello'}"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
            <rect width="3" height="9" x="7" y="7"></rect>
            <rect width="3" height="5" x="14" y="7"></rect>
          </svg>
        </div>
      </div>
    `;
  }

  override render() {
    const tooltipText = this.isPrivate 
      ? 'Private - Click to enable sharing' 
      : 'Shared - Click to make private';
      
    return html`
      <div class="shareability-footer"
        ${animate({
          keyframeOptions: {
            duration: 300,
            fill: 'both',
          },
          in: fadeIn,
        })}
      >
        <div class="share-status-pill ${this.isExpanded ? 'expanded' : ''}"
          @mouseenter=${() => this.isExpanded = true}
          @mouseleave=${() => this.isExpanded = false}
          @click=${this.togglePrivacy}
        >
          ${this.renderLockIcon()}
          ${this.renderServiceIcons()}
          <div class="lock-tooltip">${tooltipText}</div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pique-action-shareability': PiqueActionShareability;
  }
} 